import { useInfiniteQuery } from '@tanstack/react-query';
import { get } from '../api/api';
import { API_URLS } from '../constants/apiUrl';
interface IUseFetchDashboardTransactions {
  startDate?: string;
  endDate?: string;
  clickedDate?: string | null;
  userId?: string;
  relations?: string[];
  sortBy?: string[];
  extraFilters?: Record<string, string | number>;
  pageSize?: number;
  search?: string;
  status?: string;
  enabled?: boolean;
  fields?: string[];
}
export const useFetchDashboardTransactions = ({
  startDate,
  endDate,
  clickedDate,
  userId,
  relations = [],
  sortBy = [],
  extraFilters = {},
  search,
  pageSize = 25, // Default page size
  status,
  enabled = true,
  fields = [],
}: IUseFetchDashboardTransactions) => {
  const fetchTransactions = async ({ pageParam = 1 }) => {
    const queryParams = new URLSearchParams();
    if ((startDate && endDate) || clickedDate) {
      queryParams.append(
        'filter.transactionDate',
        clickedDate ? `$eq:${clickedDate}` : `$btw:${startDate},${endDate}`,
      );
    }
    if (search) queryParams.append('search', search);
    if (status) queryParams.append('filter.status', status);
    if (fields.length > 0) {
      fields.forEach((field) => queryParams.append('fields', field));
    }

    Object.entries(extraFilters).forEach(([key, value]) => {
      queryParams.append(`filter.${key}`, String(value));
    });

    relations.forEach((relation) => queryParams.append('relations', relation));
    sortBy.forEach((sort) => queryParams.append('sortBy', sort));

    queryParams.append('page', String(pageParam));
    queryParams.append('limit', String(pageSize));
    try {
      return await get(`${API_URLS.TRANSACTION_LIST}?${queryParams.toString()}`);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw new Error((error as { message: string }).message || 'Failed to fetch transactions'); // Force error throwing
    }
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    refetch,
    isFetching,
    isRefetching,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: [
      'transactions',
      startDate,
      endDate,
      userId,
      relations,
      sortBy,
      extraFilters,
      clickedDate,
      search,
      status,
    ],
    queryFn: fetchTransactions,
    enabled: enabled,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage?.meta?.totalItems ?? 0;
      const fetchedCount = allPages.flatMap((page) => page.data).length;
      return fetchedCount < totalCount ? allPages.length + 1 : undefined;
    },
  });
  return {
    data: data?.pages.flatMap((page) => page.data) ?? [],
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    refetch,
    isFetching,
    isRefetching,
  };
};
